import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  fromEvent,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { filter, map, observeOn, switchMap, tap } from 'rxjs/operators';
import {
  ClearCommand,
  LoadCommand,
  RedoCommand,
  TextbookDrawerCommand,
  UndoCommand,
  CANVAS_DEFAULT_WIDTH_PX,
  CANVAS_DEFAULT_HEIGHT_PX,
} from './textbook-drawer-commands';
import { TextbookDrawerService } from './textbook-drawer.service';
import { Tool, ToolName, ToolsMap } from './tools';
import { v4 as uuidv4 } from 'uuid';

const ERASER_PREVIEW_COLOR = 'rgba(128, 128, 128, 0.3)';
const BRUSH_PREVIEW_COLOR = 'rgba(255, 255, 255, 0)';

type Point = {x:number, y:number};

@Component({
  selector: 'gwo-textbook-drawer',
  templateUrl: './textbook-drawer.component.html',
  styleUrls: ['./textbook-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextbookDrawerComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Width of canvas context. Default: 900
   */
  @Input() width = CANVAS_DEFAULT_WIDTH_PX;

  /**
   * Height of canvas context. Default: 900
   */
  @Input() height = CANVAS_DEFAULT_HEIGHT_PX;

  /**
   * Maximum amount of states stored for undo commands. Default: 10
   */
  @Input() maxUndoLength = 10;
  @Input() isOffline!: boolean;
  @Input() isZoomed: boolean = false;
  @Input() @HostBinding("style.--header-height") headerHeight: string = '0px';
  @Input() @HostBinding("style.--footer-height") footerHeight: string = '0px';
  @Input() @HostBinding("style.--vh") vh: string|null = null;
  @Input() fullSize = false;
  @ViewChild('main') mainCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('anim') animCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('eraserCursor') eraserCursor!: ElementRef<HTMLDivElement>;
  @HostBinding('class') uniqueClass = `gwo-textbook-drawer-${uuidv4().toString()}`;
  mousePosition$ = new BehaviorSubject<Point | undefined>(undefined);
  previewCursorVisible$ = new BehaviorSubject(false);
  previewCursorSize$ = new BehaviorSubject(0);
  previewCursorColor$ = new BehaviorSubject(ERASER_PREVIEW_COLOR);
  currentCursor = 'auto';
  private dataUrlToLoad$ubject = new Subject<{ dataUrl?: string; saveState: boolean }>();
  private tool: Tool | undefined;
  private subscription = new Subscription();
  private undoStates: string[] = [];
  private currentState!: string;
  private redoStates: string[] = [];
  // prevent from duplicate events: mousedown, mousemove, mouseup right after touchEnd
  private touchEndTimeStamp!: number;

  constructor(private service: TextbookDrawerService, private ngZone: NgZone) {}

  static getMousePosition(e: MouseEvent | TouchEvent): Point {
    const { clientX: x, clientY: y } = e instanceof MouseEvent ? e : e.touches[0];
    return { x, y };
  }

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      [
        fromEvent<MouseEvent>(window, 'mousedown').pipe(tap((e) => this.onMouseDown(e))),
        fromEvent<MouseEvent>(window, 'mousemove').pipe(tap((e) => this.onMouseMove(e))),
        fromEvent<MouseEvent>(window, 'mouseup').pipe(tap((e) => this.onMouseUp(e))),
        fromEvent<TouchEvent>(window, 'touchstart', {passive: false}).pipe(tap((e) => this.onTouchStart(e))),
        fromEvent<TouchEvent>(window, 'touchmove', {passive: false}).pipe(tap((e) => this.onTouchMove(e))),
        fromEvent<TouchEvent>(window, 'touchend', {passive: false}).pipe(tap((e) => this.onTouchEnd(e))),
      ].forEach((event$: Observable<any>) => this.subscription.add(event$.subscribe()));
    });
  }

  ngAfterViewInit(): void {
    const mainCanvas = this.mainCanvasRef.nativeElement;
    this.service._setMainCanvas(mainCanvas);
    const mainCtx = mainCanvas.getContext('2d');
    const animCtx = this.animCanvasRef.nativeElement.getContext('2d');

    if (!mainCtx || !animCtx) {
      throw new Error('Textbook drawer failed initialization');
    }

    this.subscription.add(
      this.service.toolName$.subscribe((name) => {
        this.tool?.teardown();
        this.tool =
          name && new ToolsMap[name](mainCtx, animCtx, () => this.updateCurrentState());

        this.setCursor(name);
      })
    );

    this.subscription.add(
      combineLatest([this.service.toolName$, this.service.color$])
        .pipe(filter(([, color]) => !!color))
        .subscribe(([toolName, color]) => {
          this.setPreviewCursorColor(toolName, color);
          this.tool?.setColor?.(color);
        })
    );

    this.subscription.add(
      combineLatest([this.service.toolName$, this.service.size$])
        .pipe(filter(([, size]) => !!size))
        .subscribe(([, size]) => {
          this.setPreviewCursorSize(size);
          this.tool?.setSize?.(size);
        })
    );

    this.subscription.add(
      this.service.command$.subscribe((command) => this.handleCommand(command))
    );

    this.subscription.add(
      this.dataUrlToLoad$ubject
        .pipe(
          tap(() => {
            this.service._setIsLoading(true);
            this.tool?.setLocked(true);
          }),
          switchMap(({ dataUrl, saveState }) => {
            if (dataUrl) {
              return this.loadImage$(dataUrl).pipe(map(() => saveState));
            }
            this.clear();
            return of(saveState);
          }),
          tap((saveState) => {
            if (saveState) {
              this.updateCurrentState();
            } else {
              this.updateOutputs();
            }
          }),
          observeOn(asyncScheduler),
          tap(() => {
            this.service._setIsLoading(false);
            this.tool?.setLocked(false);
          })
        )
        .subscribe()
    );

    this.subscription.add(
      this.service.isLocked$.subscribe((isLocked) => this.tool?.setLocked(isLocked))
    );

    this.subscription.add(
      combineLatest([this.mousePosition$, this.previewCursorVisible$, this.previewCursorSize$])
        .pipe(
          filter(() => Boolean(this.eraserCursor.nativeElement)),
          tap(([mousePosition, cursorVisible]) => {
            const isVisible =
              Boolean(mousePosition) && Boolean(cursorVisible) ? 'visible' : 'hidden';
            const previewCursorStyle = this.eraserCursor.nativeElement.style;

            if (previewCursorStyle.visibility !== isVisible) {
              previewCursorStyle.visibility = isVisible;
            }
          }),
          filter(
            ([mousePosition, cursorVisible]) => Boolean(mousePosition) && Boolean(cursorVisible)
          )
        )
        .subscribe(([mousePosition, _, size]) => {
          const eraserShadowPosition: Point = this.calcEraserShadowPosition(mousePosition, size);
          this.eraserCursor.nativeElement.style.transform =
            'translate(' + eraserShadowPosition.x + 'px,' + eraserShadowPosition.y + 'px)';
        })
    );

    this.updateCurrentState();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.dataUrlToLoad$ubject.complete();
    this.setCursor(undefined);
    this.updateDomCursor(this.currentCursor);
  }

  private onMouseDown(e: MouseEvent): void {
    if (this.isOffline || this.isZoomed) {
      return;
    }
    if (this.touchEndTimeStamp !== e.timeStamp) {
      this.tool?.onMouseDown?.(e);
    }
  }

  private onMouseMove(e: MouseEvent): void {
    if (this.isOffline || this.isZoomed) {
      return;
    }
    if (this.touchEndTimeStamp !== e.timeStamp) {
      this.updateMousePosition(e);
      const cursor = this.isOverCanvas(e) ? this.currentCursor : 'auto';
      this.updateDomCursor(cursor);
      this.tool?.onMouseMove?.(e);
    }
  }

  private onMouseUp(e: MouseEvent): void {
    if (this.touchEndTimeStamp !== e.timeStamp) {
      this.tool?.onMouseUp?.(e);
    }

    this.eraserCursor.nativeElement.style.visibility = 'hidden';
  }

  private onTouchStart(e: TouchEvent): void {
    if (this.isOffline) {
      return;
    }
    this.tool?.onTouchStart?.(e);
  }

  private onTouchMove(e: TouchEvent): void {
    if (!!this.service.getToolName()) {
      e.preventDefault();
    }
    if (this.isOffline || this.isZoomed) {
      return;
    }
    if (this.touchEndTimeStamp !== e.timeStamp) {
      this.updateMousePosition(e);
      const cursor = this.isOverCanvas(e) ? this.currentCursor : 'auto';
      this.updateDomCursor(cursor);
      this.tool?.onTouchMove?.(e);
    }
  }

  private onTouchEnd(e: TouchEvent): void {
    this.tool?.onTouchEnd?.(e);
    this.eraserCursor.nativeElement.style.visibility = 'hidden';
    this.touchEndTimeStamp = e.timeStamp;
  }

  private handleCommand(command: TextbookDrawerCommand): void {
    if (command instanceof ClearCommand) {
      this.loadImage({ dataUrl: undefined, saveState: true });
    } else if (command instanceof LoadCommand) {
      command.state !== this.currentState && this.loadImage({ dataUrl: command.state, saveState: true });
      this.service.setSavedImageDataUrl(command.state);
    } else if (command instanceof UndoCommand) {
      this.undo();
    } else if (command instanceof RedoCommand) {
      this.redo();
    }
  }

  private updateCurrentState(): void {
    if (this.undoStates.length >= this.maxUndoLength) {
      this.undoStates.shift();
    }
    if (this.currentState) {
      this.undoStates.push(this.currentState);
    }
    this.currentState = this.getImageUrl();
    this.redoStates = [];
    this.updateOutputs();
  }

  private getImageUrl(): string {
    return this.mainCanvasRef?.nativeElement.toDataURL() || '';
  }

  private loadImage({ dataUrl, saveState }: { dataUrl?: string; saveState: boolean }): void {
    this.dataUrlToLoad$ubject.next({ dataUrl, saveState });
  }

  private clear(): void {
    const canvas = this.mainCanvasRef?.nativeElement;
    const ctx = canvas.getContext('2d');
    if (canvas && ctx) {
      const prevCompositeOperation = ctx.globalCompositeOperation;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = prevCompositeOperation;
    }
  }

  private undo(): void {
    if (this.undoStates.length) {
      const lastUndoState = this.undoStates.pop();

      if (!lastUndoState) {
        return;
      }
      this.redoStates.push(this.currentState);
      this.currentState = lastUndoState;
      this.loadImage({ dataUrl: this.currentState, saveState: false });
    }
  }

  private redo(): void {
    if (this.redoStates.length) {
      const lastRedoState = this.redoStates.pop();
      if (!lastRedoState) {
        return;
      }
      this.undoStates.push(this.currentState);
      this.currentState = lastRedoState;
      this.loadImage({ dataUrl: this.currentState, saveState: false });
    }
  }

  private updateOutputs(): void {
    this.service._setCurrentState(this.currentState);
    this.service._setUndoLength(this.undoStates.length);
    this.service._setRedoLength(this.redoStates.length);
  }

  private loadImage$(dataUrl: string): Observable<string> {
    return new Observable((observer) => {
      const ctx = this.mainCanvasRef?.nativeElement.getContext('2d');
      if (!ctx) {
        observer.next(dataUrl);
        observer.complete();
        return;
      }

      const startTime = new Date().getTime();
      const img = new Image();
      img.src = dataUrl;
      img.onload = () => {
        const ctxWidth = ctx.canvas.width;
        const ctxHeight = ctx.canvas.height;
        const widthRatio = ctxWidth / img.naturalWidth;
        const heightRatio = ctxHeight / img.naturalHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        const imgHeight = img.naturalHeight * ratio;
        const imgWidth = img.naturalWidth * ratio;
        const startX = (ctxWidth - imgWidth) / 2;
        const startY = (ctxHeight - imgHeight) / 2;

        this.clear();
        const prevCompositeOperation = ctx.globalCompositeOperation;
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(img, startX, startY, imgWidth, imgHeight);
        ctx.globalCompositeOperation = prevCompositeOperation;
        const endTime = new Date().getTime();
        console.log(`Image loaded in: ${endTime - startTime} ms`);
        observer.next(dataUrl);
        observer.complete();
      };

      return () => {
        img.src = '';
      };
    });
  }

  private updateMousePosition(e: MouseEvent | TouchEvent): void {
    const position = this.isOverCanvas(e) ? TextbookDrawerComponent.getMousePosition(e) : undefined;
    this.mousePosition$.next(position);
  }

  private setPreviewCursorColor(toolName: ToolName | undefined, color: string): void {
    const previewColor =
      toolName === ToolName.Eraser
        ? ERASER_PREVIEW_COLOR
        : toolName === ToolName.Brush
        ? BRUSH_PREVIEW_COLOR
        : color;

    this.previewCursorColor$.next(previewColor);
  }

  private setPreviewCursorSize(size: number): void {
    const initialWidth = this.mainCanvasRef.nativeElement.width;
    const currentWidth = this.mainCanvasRef.nativeElement.clientWidth;
    this.previewCursorSize$.next((size * currentWidth) / initialWidth);
  }

  private setCursor(toolName: ToolName | undefined): void {
    let cursor = 'auto';
    this.previewCursorVisible$.next(false);
    if (toolName === ToolName.Brush) {
      const img =
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAaCAYAAACpSkzOAAABVklEQVRIS7XWy3GDMBAA0F2Le9xBSojdgdOBXUGSu8QoFYA70IDuIR24g7iDUALpAN/RbIYMZLD5CmTOoDf7YVcIjh8hxCsABIgYRlH0WR+PLp0K+WicmTHGtkqp3BnUgdTeKY7jgxNoAKmx98WQlHJjjPkeKUG2CKqQLwBYj0BvsyEbJI7jZBZki5TRWkNzEGtoLmIFTUQuRLTXWp9vm2NS6qYijLGdUirt6sBRyAUymjpXyCDkEumFXCO9kBAiB4CHgbFyGSr8aDNIKddFUWw8z8uNMQkAPHV8ZI20IvJ9PySigIhCrfWRc54g4ksDm4W0oGbKiCjVWm+FEOV/UUY2G7mCupYXIh5Xq9XJGHO2rUnvZBBCZADwePsCET17npeWe3/J/eJvMvSs4h8ACMtdsgS4ugVV3aaqwjsF/iHO+Q4RA8bYwRizdxVBq0ZVS2f3AmrwF78u25+hop4YAAAAAElFTkSuQmCC';
      cursor = `url('${img}') 0 24, crosshair`;
      this.previewCursorVisible$.next(true);
    } else if (toolName === ToolName.Eraser) {
      const img =
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAACZElEQVRYR7VX0ZHaMBTch1zAdRBKIBWEVJBLB9w/YpQKcunAsfx/XAUhFRxUcFwFIR0k/9Yo84SdsY0sCxD+YQbL2vW+t6tnQqJLSrkA8BXARgjxnOf5PmZrilk0tqYGf+qtO1hr8yzLfuZ5fhja42oCA+AdPGstq7H2kbmKQAy45803RLSZTCaszJ+LCSilZsaYFwB3YyUaui+EeH8RgRTgAL5orfOzCaQAt9Y+l2W5kFI+nUUgNTiARTSBFOAA3oQQc2PMPQBn2ygCtwKPIpAI/C+/OQP2nRNU4NbgQQUSgcNa+znLsq0x5hXAtJcJv70KKKXujDG/rgmZGuhBCLGpquqFiGY9cFeWEwIMPvDAWYFHRN+LolBSyh8AuOv758PHsiy3HQKpwNtBw173MH/QWq87PZAKHMBOaz1frVaP1lqeDzpXo0zzp1MgIfhJ0LTRG2Xa/1FCcG6qaVVVMyLiU7J/vWmt+414TEKl1LSORw6LT2d123HxYNDUezll+Pw/KYk7kY4Dwo4X1IowkXsi4t93Y4T4XAdwGLCuIzc0I5KU0jYA9ei0qUcnN1TWgeQIAfjg6+iA18HkQgNqh0Bvc5aLx6dtMz551Ml5qFgul6+eoOHt/tttSMUQgX5w7JkMk9Ja75qbXEKf14noW1EUj2PliybgUYdHqnW7hK1SuolnDJzvX0oAzRt6CLggigG/BYFBu13dAyf+rWvcUoDtNgt9BflIuCTkELLWzmN976TrEgh6PVQO33HMHxwh37v9egRG7TZYghC7UCq2CCjOgtimOynlOQ+2zwwi2sf4fGz/f553lZuFZ4l4AAAAAElFTkSuQmCC';
      cursor = `url('${img}') 0 29, crosshair`;
      this.previewCursorVisible$.next(true);
    } else if (toolName) {
      cursor = 'crosshair';
    }

    this.currentCursor = cursor;
  }

  private isOverCanvas(e: MouseEvent | TouchEvent): boolean {
    const { left, top, width, height } = this.animCanvasRef.nativeElement.getBoundingClientRect();
    const { x, y } = TextbookDrawerComponent.getMousePosition(e);
    return x >= left && x <= left + width && y >= top && y <= top + height;
  }

  private updateDomCursor(cursor: string): void {
    const bodyStyle = document.body.style;
    if (bodyStyle.cursor !== cursor) {
      bodyStyle.cursor = cursor;
    }
  }

  private calcEraserShadowPosition(mousePosition: Point | undefined, size: number): Point {
    const drawerClientRect = document.querySelector(`.${this.uniqueClass}`)?.getBoundingClientRect();
    const offsetY = drawerClientRect?.top as number + 1;
    const offsetX = drawerClientRect?.left as number + 1;
    return {
      x: Number(mousePosition?.x) - offsetX - size / 2,
      y: Number(mousePosition?.y) - offsetY - size / 2
    }
  }
}
